import { Form, Modal, Typography } from "antd";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import SelectProduct from "~pages/Users/components/SelectProduct/SelectProduct.tsx";
import { PermissionProducts } from "~pages/Users/enums/permissionProducts";
import useInviteNotifyUser from "~pages/Users/hooks/useInviteNotifyUser.ts";
import { getUserProducts } from "~pages/Users/utilities";
import type { User } from "~types/user.ts";

type Props = {
  user: User | null;
  preferredProduct?: PermissionProducts;
  onClose: () => void;
};

const InviteNotifyModal = ({ user, preferredProduct, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm<{ selectedProduct: string }>();
  const { mutateAsync: inviteNotifyUser } = useInviteNotifyUser();

  const isOpen = user !== null;
  const availableProducts = user ? getUserProducts(user) : undefined;
  const firstProduct = availableProducts?.values().next().value;
  const defaultProduct =
    (preferredProduct && availableProducts?.has(preferredProduct) ? preferredProduct : firstProduct) ?? PermissionProducts.Competitions;
  const initialValues = { selectedProduct: defaultProduct };

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({ selectedProduct: defaultProduct });
    }
  }, [isOpen, defaultProduct, form]);

  if (!user) {
    return null;
  }

  const actionType = user.isPending ? "invite" : "notify";

  const handleFinish = async (values: { selectedProduct: string }) => {
    await inviteNotifyUser({
      userId: user.userId,
      isPendingUser: user.isPending,
      selectedProduct: values.selectedProduct,
    });
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      width={600}
      title={formatMessage({ id: `permissions.user.actions.${actionType}` })}
      okText={formatMessage({ id: actionType })}
      onCancel={onClose}
      okButtonProps={{ htmlType: "submit" }}
      modalRender={(dom) => (
        <Form
          form={form}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          clearOnDestroy
          onFinish={handleFinish}
          initialValues={initialValues}
        >
          {dom}
        </Form>
      )}
      centered
      destroyOnHidden
    >
      <Typography.Paragraph>{formatMessage({ id: `permissions.user.actions.${actionType}.content` })}</Typography.Paragraph>
      <Form.Item
        required
        name="selectedProduct"
        label={formatMessage({ id: "permissions.user.selectProduct" })}
        tooltip={formatMessage({ id: `form.help.selectProduct.on.${actionType}.action` })}
        rules={[
          {
            required: true,
            message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "permissions.user.selectProduct" }) }),
          },
        ]}
      >
        <SelectProduct availableProducts={availableProducts} />
      </Form.Item>
      {user.isPending && <Typography.Paragraph>{formatMessage({ id: `permissions.user.actions.invite.note` })}</Typography.Paragraph>}
    </Modal>
  );
};

export default InviteNotifyModal;
